import { Suspense, useMemo } from 'react';
import {
  Box,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderIconTile,
  PageHeaderTabs,
  PageHeaderTitle,
  TabNav,
  TabNavList,
  TabNavTab,
} from '@customerio/pluma-components/react';
import { useQueryState } from 'nuqs';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { isEmptyString } from '@/utils/string';
import { useAnalytics } from '@/services/analytics';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import type { AttributeScope } from '@/services/db/data-index/entity';
import { DataIndexTable } from './components/data-index-table';
import { ExportMenu } from './components/export-menu';
import { toTableRow } from './utils/table-row';
import { ObjectTypeSelect } from './components/object-type-select';
import { useDataIndexList, useEnabledObjectTypes, useEnabledObjectTypesOptional, useRowSelection } from './hooks';
import { activeTabFor, DATA_INDEX_TABS, dataIndexListPath } from './urls';
import type { DataIndexType } from './utils/types';

/**
 * The data index's type list: every attribute or event a workspace has seen,
 * with what wrote it, what it is tagged with, and how much of the workspace
 * carries it.
 */
type DataIndexListPageProps = {
  type: DataIndexType;
};

/**
 * The view event the page this replaces fires on every transition into a
 * browser.
 *
 * Keyed on the path rather than the whole URL: paging and filtering are the
 * same view of the same browser, and firing again for each would drown the
 * one signal the event carries. Which browser was opened, and whether a
 * search was narrowing it when it was.
 */
function useDataIndexViewTracking(type: DataIndexType, isFilteredByName: boolean) {
  const analytics = useAnalytics();

  usePageViewTracking(
    () => {
      void analytics.track('logs', 'dataIndexViewed', {
        'Data Type': type === 'events' ? 'Events' : 'Attributes',
        'Filtered By Name': isFilteredByName ? 'Yes' : 'No',
      });
    },
    true,
    { ignoreSearch: true },
  );
}

export default function DataIndexListPage({ type }: DataIndexListPageProps) {
  const workspaceId = requireWorkspaceId();
  // Read straight from the URL rather than from the list's own state: a
  // shared or bookmarked link already carries the search, and the event
  // would otherwise report every narrowed view as unnarrowed.
  const [search] = useQueryState('search');
  useDataIndexViewTracking(type, !isEmptyString(search ?? ''));

  return (
    <Page>
      <DocumentTitle title="Data index" />
      <DataIndexHeader type={type} workspaceId={workspaceId} />
      <PageBody>
        <Suspense fallback={<LoadingPage />}>
          <DataIndexListBody type={type} />
        </Suspense>
      </PageBody>
    </Page>
  );
}

type DataIndexHeaderProps = {
  type: DataIndexType;
  workspaceId: number;
};

function DataIndexHeader({ type, workspaceId }: DataIndexHeaderProps) {
  const activeTab = activeTabFor(type);
  const objectTypes = useEnabledObjectTypesOptional();

  return (
    <PageHeader>
      <PageHeaderIconTile name="data-index" />
      <PageHeaderTitle>Data index</PageHeaderTitle>
      <PageHeaderDescription>
        A snapshot of all the attributes and events in your Customer.io account.
      </PageHeaderDescription>
      <PageHeaderTabs>
        <TabNav aria-label="Data index">
          <TabNavList>
            {DATA_INDEX_TABS.map((tab) => (
              <TabNavTab
                key={tab.type}
                href={dataIndexListPath(String(workspaceId), tab.type)}
                isActive={activeTab === tab.type}
              >
                {tab.label}
              </TabNavTab>
            ))}
          </TabNavList>
        </TabNav>
      </PageHeaderTabs>
      <PageHeaderActions>
        <ExportMenu workspaceId={workspaceId} objectTypes={objectTypes} />
      </PageHeaderActions>
    </PageHeader>
  );
}

type DataIndexListBodyProps = {
  type: DataIndexType;
};

function DataIndexListBody({ type }: DataIndexListBodyProps) {
  const list = useDataIndexList(type);
  const objectTypes = useEnabledObjectTypes();
  const environment = useEnvironmentOptional();
  const selection = useRowSelection(list.rowSetKey);

  const rows = useMemo(() => list.rows.map(toTableRow), [list.rows]);

  const objectTypeName = objectTypeNameOf(objectTypes, list.scope);

  return (
    <>
      {type !== 'events' ? (
        <Box mb="100">
          <ObjectTypeSelect value={list.scopeSelection} objectTypes={objectTypes} onChange={list.setScopeSelection} />
        </Box>
      ) : null}
      <DataIndexTable
        workspaceId={list.workspaceId}
        kind={list.kind}
        scope={list.scope}
        rows={rows}
        tags={list.tags}
        identifiers={environment.data?.environment?.identifiers}
        rowUsage={list.rowUsage}
        isRowUsageLoading={list.isRowUsageLoading}
        isTotalPeopleLoading={list.isTotalPeopleLoading}
        isProfileCountsFetching={list.isProfileCountsFetching}
        isObjectCountsLoading={list.isObjectCountsLoading}
        isLoading={list.isLoading}
        narrowing={list.narrowing}
        catalogue={list.catalogue}
        totals={list.totals}
        page={list.page}
        onPageChange={list.setPage}
        rowSelection={selection.rows}
        onRowSelectionChange={selection.setRows}
        profileCounts={list.profileCounts}
        totalPeople={list.totalPeople}
        objectCounts={list.objectCounts}
        totalObjects={list.totalObjects}
        objectTypeName={objectTypeName}
      />
    </>
  );
}

/** What to call the objects a scope counts against, falling back to the generic noun. */
function objectTypeNameOf(objectTypes: readonly { id: string; name: string }[], scope: AttributeScope): string {
  if (scope.kind === 'profile') {
    return 'Objects';
  }
  return objectTypes.find((objectType) => Number(objectType.id) === scope.objectTypeId)?.name ?? 'Objects';
}
